/* PSM redesign — multi-axis "Filter & pick" variant selector (Option B).
 *
 * The visible chips + table are a UI layer over the HIDDEN native per-option
 * radios (.psm-native-options .option-selector). variant-picker.js keeps full
 * ownership of variant resolution: we only set the native radios and dispatch a
 * `change` event, which triggers its price/SKU/media/URL/ATC/quantity updates.
 *
 * Interaction:
 *  - Click a chip → toggle that axis's filter; table narrows to matching rows.
 *  - Narrowing to exactly one row → auto-selects it.
 *  - Click any visible row → selects that variant.
 */
(function () {
  function initFilter(vfilter) {
    if (vfilter.dataset.vfReady) return;
    vfilter.dataset.vfReady = "1";

    const picker = vfilter.closest("variant-picker");
    if (!picker) return;
    // native selectors, in product option order
    const selectors = Array.from(
      picker.querySelectorAll(".psm-native-options .option-selector")
    );
    if (!selectors.length) return;

    // The theme swaps product media by grouping images per finish and switching
    // groups from an `on:variant:change` listener on <media-gallery>. That
    // listener doesn't fire for this multi-axis filter layout, so after each
    // selection we call the gallery's handler directly. It re-reads the native
    // radios (which commit() has just set) and shows the chosen finish's image.
    // Idempotent: a no-op when the finish group hasn't changed, and safely
    // ignored when grouping is disabled or the gallery isn't ready.
    const productScope = picker.closest(".js-product") || document;
    function syncGallery() {
      const gallery = productScope.querySelector("media-gallery");
      if (!gallery || typeof gallery.onVariantChange !== "function") return;
      requestAnimationFrame(() => {
        try {
          gallery.onVariantChange({ detail: { variant: {} } });
        } catch (e) {
          /* grouping disabled / gallery not ready — nothing to sync */
        }
      });
    }

    const chips = Array.from(vfilter.querySelectorAll(".vf-chip"));
    const rows = Array.from(vfilter.querySelectorAll(".vf-row"));
    const scroll = vfilter.querySelector(".vf-scroll");
    const readout = vfilter.querySelector("[data-vf-readout]");
    const countEls = Array.from(
      vfilter.querySelectorAll("[data-vf-count], [data-vf-count-b]")
    );

    const filters = {}; // axis(String) -> value(String); one value per axis

    function rowMatches(row, ignoreAxis) {
      return Object.keys(filters).every(
        (ax) => ax === ignoreAxis || row.dataset["opt" + ax] === filters[ax]
      );
    }

    function setCount(n) {
      countEls.forEach((el) => (el.textContent = n));
    }

    function apply(autoSelect) {
      let visible = 0;
      let lastVisible = null;
      rows.forEach((row) => {
        const match = rowMatches(row, null);
        row.classList.toggle("vf-hidden", !match);
        if (match) {
          visible += 1;
          lastVisible = row;
        }
      });
      setCount(visible);

      // dim chips whose value would yield no rows given the OTHER active filters
      chips.forEach((chip) => {
        const ax = chip.dataset.axis;
        const val = chip.dataset.value;
        const compat = rows.some(
          (row) => row.dataset["opt" + ax] === val && rowMatches(row, ax)
        );
        chip.classList.toggle("vf-dim", !compat);
        chip.classList.toggle("vf-on", filters[ax] === val);
      });

      // empty-state guard
      let empty = vfilter.querySelector(".vf-empty");
      if (visible === 0) {
        if (!empty) {
          empty = document.createElement("div");
          empty.className = "vf-empty";
          empty.textContent = "No variant matches those options.";
          scroll.appendChild(empty);
        }
      } else if (empty) {
        empty.remove();
      }

      if (autoSelect && visible === 1 && lastVisible && !lastVisible.classList.contains("sel")) {
        commit(lastVisible);
      }
    }

    function commit(row) {
      // set each native option radio to this variant's value, then fire change
      selectors.forEach((sel, i) => {
        const want = row.dataset["opt" + i];
        sel.querySelectorAll("input.js-option").forEach((r) => {
          r.checked = r.value === want;
        });
      });
      rows.forEach((r) => r.classList.toggle("sel", r === row));

      if (readout) {
        const skuEl = row.querySelector(".vf-sku");
        if (skuEl) readout.textContent = skuEl.textContent;
      }

      const last = selectors[selectors.length - 1];
      const checked = last.querySelector("input:checked");
      if (checked) checked.dispatchEvent(new Event("change", { bubbles: true }));

      syncGallery();
    }

    chips.forEach((chip) => {
      chip.addEventListener("click", () => {
        const ax = chip.dataset.axis;
        const val = chip.dataset.value;
        if (filters[ax] === val) delete filters[ax];
        else filters[ax] = val;
        apply(true);
      });
    });

    rows.forEach((row) => {
      row.addEventListener("click", () => commit(row));
    });

    apply(false);
  }

  function boot() {
    document.querySelectorAll(".psm-vfilter").forEach(initFilter);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
